import { MatDialog } from "@angular/material/dialog";
import {
  RenamedSectionResult,
  DuplicatedSectionResult,
  DeletedSectionResult,
  FieldDefinitionChangeResult,
  FormComponent,
  FormResult,
  Field,
  FormReturnType,
  NaturalFormFlavour,
  NaturalFormStyle,
  NaturalFormConfig,
} from "@cotecna/dynamic-view-engine";
import { Subscription } from "rxjs";
import {
  DynamicFormDialogComponent,
  DynamicFormResult,
} from "./components/natural-form-dialog/dynamic-form-dialog.component";

export abstract class NaturalFormDialogBase {
  protected formConfig = {
    displayMode: NaturalFormStyle.material,
    flavor: NaturalFormFlavour.informative,
    notifyFieldChange: false,
    returnType: FormReturnType.entity,
  } as NaturalFormConfig;

  private formSubmittedSubscription: Subscription;
  private fieldChangeSubscription: Subscription;
  private sectionRenameSubscription: Subscription;
  private sectionDuplicateSubscription: Subscription;
  private sectionDeleteSubscription: Subscription;
  private fieldRenameSubscription: Subscription;
  private fieldDuplicateSubscription: Subscription;
  private fieldDeleteSubscription: Subscription;
  private photoTakenSubscription: Subscription;
  private fullPictureRequestedSubscription: Subscription;

  constructor(public dialog: MatDialog) {}

  abstract onFormSubmitted(result: FormResult);
  abstract onFieldChange(field: Field);
  abstract onRenameSection(result: RenamedSectionResult);
  abstract onDuplicateSection(result: DuplicatedSectionResult);
  abstract onDeleteSection(result: DeletedSectionResult);
  abstract onRenameField(result: FieldDefinitionChangeResult);
  abstract onDuplicateField(result: FieldDefinitionChangeResult);
  abstract onDeleteField(result: FieldDefinitionChangeResult);
  abstract onPhotoTaken(result: any);
  abstract onFullPictureRequested(result: string);

  protected async openFormDialog(
    icon: string,
    title: string,
    sections: any[],
    controls: any[],
  ): Promise<DynamicFormResult> {
    const dialogRef = this.dialog.open(DynamicFormDialogComponent, {
      data: {
        icon,
        title,
        formConfig: this.formConfig,
        sections,
        controls,
        confirmButtonText: "SAVE",
        cancelButtonText: "CANCEL",
        allowConfirmWithInvalidFrom: false,
      },
      autoFocus: false,
      disableClose: false,
      panelClass: "dynamic-form-dialog",
    });

    dialogRef.afterOpened().subscribe(() => {
      const form: FormComponent = dialogRef.componentInstance.naturalForm;
      this.subscribeToFormEvents(form);
    });
    
    return await dialogRef
      .afterClosed()
      .toPromise()
      .then((result: DynamicFormResult) => {
        this.unsubscribeAllFormEvents();
        return result;
      });
  }

  private subscribeToFormEvents(form: FormComponent) {
    //detect form submitted
    this.formSubmittedSubscription = form.formSubmitted.subscribe((result) =>
      this.onFormSubmitted(result),
    );

    //detect field change
    this.fieldChangeSubscription = form.fieldChange.subscribe((field) =>
      this.onFieldChange(field),
    );

    //section actions
    this.sectionRenameSubscription = form.sectionRenamed.subscribe((result) =>
      this.onRenameSection(result),
    );
    this.sectionDuplicateSubscription = form.sectionDuplicated.subscribe(
      (result) => this.onDuplicateSection(result),
    );
    this.sectionDeleteSubscription = form.sectionDeleted.subscribe((result) =>
      this.onDeleteSection(result),
    );

    //field actions
    this.fieldRenameSubscription = form.fieldRenamed.subscribe((result) =>
      this.onRenameField(result),
    );
    this.fieldDuplicateSubscription = form.fieldDuplicated.subscribe((result) =>
      this.onDuplicateField(result),
    );
    this.fieldDeleteSubscription = form.fieldDeleted.subscribe((result) =>
      this.onDeleteField(result),
    );

    //photo taken action
    this.photoTakenSubscription = form.photoTaken.subscribe((result) =>
      this.onPhotoTaken(result),
    );

    //photo requested action
    this.fullPictureRequestedSubscription = form.fullPictureRequested.subscribe(
      (result) => this.onFullPictureRequested(result),
    );
  }

  private unsubscribeAllFormEvents() {
    this.formSubmittedSubscription?.unsubscribe();
    this.fieldChangeSubscription?.unsubscribe();
    this.sectionRenameSubscription?.unsubscribe();
    this.sectionDuplicateSubscription?.unsubscribe();
    this.sectionDeleteSubscription?.unsubscribe();
    this.fieldRenameSubscription?.unsubscribe();
    this.fieldDuplicateSubscription?.unsubscribe();
    this.fieldDeleteSubscription?.unsubscribe();
    this.photoTakenSubscription?.unsubscribe();
    this.fullPictureRequestedSubscription?.unsubscribe();
  }
}
